import {
  Box, Container, Typography,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { motion } from 'motion/react'
import { useState } from 'react'
import UseTranslation from '../../../hooks/UseTranslation'
import CardWithSpace from '../../../components/Cards/CardWithSpace'
import HoverTooltip from '../../../components/PageHelpers/HoverTooltip'
import {
  decodeHtmlString, getFormattedDateWithDay, getFormattedDateWithoutDay, getStatusColor,
} from '../../../utils/helpers'
import TitleWithIcon from '../../../components/Cards/helpers/TitleWithIcon'
import SeeMoreText from '../../../components/Cards/helpers/SeeMoreText'
import { ReactComponent as ReceivingDate } from '../../../images/ReceivingDate.svg'
import { ReactComponent as RequestType } from '../../../images/RequestType.svg'
import { ReactComponent as Manager } from '../../../images/Manager.svg'
import { ReactComponent as DecisionMaking } from '../../../images/DecisionMaking.svg'

import NoDataFound from '../../../components/PageHelpers/NoDataFound'
import { cardVariants } from '../../../utils/animation'
import { selectNotificationActionHistory } from '../../../redux/features/notifications/notificationActionHistory/notificationActionHistorySlice'
import SvgIconSize from '../../../components/Cards/helpers/SvgIconSize'
import NotificationDetailModel from '../helper/NotificationDetailModel'
import { getNotificationActionHistoryList, notificationActionHistoryResetAlertHandler } from '../../../redux/features/notifications/notificationActionHistory/notificationActionHistoryThunk'
import LoaderWithErrorHandler from '../../../components/PageHelpers/LoaderWithErrorHandler'
import ActionHistoryCardListSkeleton from './ActionHistoryCardListSkeleton'
import LoadMoreBtn from '../../../components/PageHelpers/LoadMoreBtn'

const ActionHistoryCardList = () => {
  const notificationActionHistoryStore = useSelector(selectNotificationActionHistory)
  const { t } = UseTranslation()
  const [notificationDetailModelState, setNotificationDetailModelState] =
  useState({ status: false, data: null })

  return (
    <Container maxWidth={false} sx={styles.container} data-testid="actionHistoryListContainer">
      {notificationActionHistoryStore?.data?.map((notification, index) => (
        <Box
          key={notification?.id}
          component={motion.div}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          viewport={{ once: true, amount: 0.3 }}
          custom={index}
        >

          <CardWithSpace
            customStyles={styles.card}
            data-testid="history-item"
            onClick={() => setNotificationDetailModelState({ status: true, data: notification })}
            isClickable
          >
            <Box sx={styles.cardsContainer}>
              <Box sx={styles.cardHeader}>
                <TitleWithIcon
                  title={(
                    <Box sx={styles.cardTitle}>
                      <Typography variant="body1" sx={styles.cardTitleName}>
                        <Box component="span" sx={styles.cardTitleCode}>
                          {`#${notification?.employee?.code}`}
                        </Box>
                        {` - ${notification?.employee?.name}`}
                      </Typography>
                    </Box>
                   )}
                  icon={<Manager />}
                  iconSize="sm"
                  customStyle={styles.stackFlex}
                />
              </Box>
              <SeeMoreText
                text={decodeHtmlString(notification?.description)}
                customStyle={styles.mobileNotesInfo}
              />

              <Box sx={styles.cardInfoContainer}>

                <Box sx={styles.rowFlex}>
                  <TitleWithIcon
                    title={(
                      <Box sx={styles.requestedOnContainer}>
                        <Typography variant="body1" sx={styles.requestedOnDate}>
                          {`${notification?.source_type?.value}`}
                        </Typography>
                      </Box>
                   )}
                    icon={(

                      <SvgIconSize
                        icon={(
                          <HoverTooltip title={t?.['Request Type']}>
                            <RequestType />
                          </HoverTooltip>
                      )}
                        size="sm"
                      />
              )}
                    customStyle={styles.iconWithText}
                  />
                </Box>

                <Box sx={styles.rowFlex}>
                  <TitleWithIcon
                    title={(
                      <Box sx={styles.requestedOnContainer}>
                        <Typography variant="body1" sx={styles.requestedOnDate}>
                          {getFormattedDateWithDay(notification?.action_time)}
                        </Typography>
                      </Box>
                   )}
                    icon={(
                      <SvgIconSize
                        icon={(
                          <HoverTooltip title={t?.['Receiving Date']}>
                            <ReceivingDate />
                          </HoverTooltip>
                      )}
                        size="sm"
                        iconColor="red"
                      />
              )}
                    customStyle={styles.iconWithText}
                  />
                </Box>
                <Box sx={styles.rowFlex}>
                  <TitleWithIcon
                    title={(
                      <Box sx={styles.requestedOnContainer}>
                        <Typography variant="body1" sx={styles.requestedOnDate}>
                          <Typography component="span" sx={{ color: getStatusColor(notification?.status?.key, t)?.color }}>
                            {notification?.status?.value}
                          </Typography>
                          {` ${t?.['by You on']} `}
                          {getFormattedDateWithoutDay(notification?.action_time)}
                        </Typography>
                      </Box>
                   )}
                    icon={(
                      <SvgIconSize
                        icon={(
                          <HoverTooltip title={t?.['Action Taken']}>
                            <DecisionMaking />
                          </HoverTooltip>
                      )}
                        size="sm"
                      />
              )}
                    customStyle={styles.iconWithText}
                  />
                </Box>
              </Box>
            </Box>
          </CardWithSpace>
        </Box>
      ))}
      <NoDataFound store={notificationActionHistoryStore} />

      <NotificationDetailModel
        modalStatus={notificationDetailModelState}
        setModalStatus={setNotificationDetailModelState}
      />
 <LoaderWithErrorHandler
          store={notificationActionHistoryStore}
          resetAlertHandler={notificationActionHistoryResetAlertHandler}
          Skeleton={ActionHistoryCardListSkeleton}
        />
        <LoadMoreBtn
          store={notificationActionHistoryStore}
          actionToDispatch={getNotificationActionHistoryList}
          sendQueryAsObject
        />
    </Container>
  )
}

export const styles = {
  container: {
    paddingInline: { xs: '10px', sm: '0px' },
    marginBottom: { xs: '120px', sm: '0px' },
  },
  card: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    borderRadius: '12px',
    marginTop: { xs: '5px', sm: '16px' },
    height: { sm: '150px' },
    padding: { xs: '0 5px', sm: '0px 44px' },
    cursor: 'pointer',
  },
  cardsContainer: {
    padding: { xs: '7px', sm: '15px 0px' },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: { xs: 'space-around', sm: 'flex-start' },
    width: '100%',
    gap: { xs: '0px', sm: '8px' },
  },
  cardHeader: {
    marginBottom: { xs: '5px', md: '0px' },
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    padding: { xs: '0px', md: '0px 10px 0px 0px', lg: '0px' },
    gap: { xs: '0px', sm: '14px' },
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  cardTitleCode: {
    color: { xs: '#000', sm: '#AFAFAF' },
  },
  cardTitleName: {
    fontSize: { xs: '14px', sm: '18px' },
  },
  stackFlex: {
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    '& .svg-icon': {
      display: { xs: 'none', sm: 'block' },
    },
    '& path': {
      fill: '#29cc8b',
    },
  },
  notesInfo: {
    display: { xs: 'none', md: 'block' },
    alignItems: 'center',
    paddingLeft: { xs: '0px', md: '20px' },
    my: { xs: '5px', md: '0px' },
    color: '#AFAFAF',
    '& span': {
      overflowWrap: 'break-word',
      paddingRight: { xs: '10px', md: '0px' },
    },
    '& p': {
      margin: { xs: '0px', md: '5px' },
      fontSize: { xs: '11px', sm: '16px' },
    },
  },
  mobileNotesInfo: {
    alignItems: 'center',
    paddingLeft: { xs: '0px', md: '20px' },
    my: { xs: '5px', md: '0px' },
    color: '#AFAFAF',
    '& span': {
      overflowWrap: 'break-word',
    },
    '& p': {
      margin: { xs: '0px', md: '5px' },
      fontSize: { xs: '12px', sm: '14px' },
    },
  },
  cardInfoContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
  },
  rowFlex: {
    maxWidth: { xs: 'unset', sm: '350px' },
    width: { xs: 'unset', sm: '100%' },
    display: 'flex',
    justifyContent: 'space-between',
    padding: { xs: '3px 0px 0px', md: '0px 15px 0px 0px' },
  },
  iconWithText: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '10px',
    marginRight: '0px',
    alignItems: 'flex-start',
    '& span': {
      fontSize: { xs: '12px', sm: '13px' },
    },
  },
  requestedOnContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& p': {
      fontSize: { xs: '12px', sm: '14px' },
    },
  },
  requestedOnDate: {
    marginLeft: '5px',
    direction: 'ltr /* @noflip */',
  },
  cardAction: {
    display: 'flex',
    justifyContent: { xs: 'center', sm: 'flex-end' },
    alignItems: 'center',
    my: { xs: '5px', sm: '0px' },
    gap: { xs: '98px', sm: '0px' },
    borderTop: { xs: '1px solid #ECEDF1', sm: 'none' },
    '& p': {
      display: { xs: 'block', sm: 'none' },
      fontSize: { xs: '13px', sm: '14px' },
      marginLeft: { xs: '5px', sm: '0px' },
      fontWeight: 800,
    },
    '& svg': {
      width: { xs: 20, sm: 40 },
      height: { xs: 20, sm: 40 },
    },
  },
  approveButton: {
    color: '#29CC8B',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#fff',
    },
    margin: '0 5px',
  },
  rejectButton: {
    color: '#F44336',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#fff',
    },
    margin: '0 5px',
  },
}

export default ActionHistoryCardList
